import { RollInput } from './MultiPlayerModel';

export class AIPlayer {
  public readonly userId: string = 'ai_robo_roller';
  public readonly userName: string = 'Robo Roller';

  private isThinking: boolean = false;
  private thinkingTimer: number | null = null;

  // Game state references
  private onMoveCallback?: (rollInput: RollInput) => void;

  // Configurable AI parameters - adjust these to tune AI behavior
  public config = {
    // Thinking time range (milliseconds)
    minThinkingTime: 900,
    maxThinkingTime: 1500,

    // Base velocity ranges (px/s, based on actual human gesture analysis)
    baseVelocityYMin: -800,   // minimum upward velocity (strongest)
    baseVelocityYRange: 400,   // range on top of min 
    baseVelocityXRange: 30,    // lateral velocity range 

    // Accuracy settings
    offShotChance: 0.02,      // 2% chance of slightly off shot
    offShotXRange: 86,        // lateral deviation for off shots
    offShotYRange: 450,        // velocity variation for off shots

    // Spin settings
    spinRange: 0.1,             // spin range

    // Physics
    speedScale: 0.55          // matches PhysicsEngine speedScale
  };

  /**
   * Set the callback function that will be called when AI makes a move
   */
  setMoveCallback(callback: (rollInput: RollInput) => void) {
    this.onMoveCallback = callback;
  }

  /**
   * Tell the AI it's their turn to play
   */
  startTurn(round: number, ballIndex: number, ballStartPos: { x: number; y: number }, tiltX: number) {
    if (this.isThinking) return;

    this.isThinking = true;

    // Random thinking time based on config
    const thinkingTime = this.config.minThinkingTime +
                        Math.random() * (this.config.maxThinkingTime - this.config.minThinkingTime);

    this.thinkingTimer = window.setTimeout(() => {
      this.makeMove(round, ballIndex, ballStartPos, tiltX);
      this.isThinking = false;
    }, thinkingTime);
  }

  /**
   * Cancel AI turn (if game is interrupted)
   */
  cancelTurn() {
    if (this.thinkingTimer) {
      clearTimeout(this.thinkingTimer);
      this.thinkingTimer = null;
    }
    this.isThinking = false;
  }

  /**
   * Generate a realistic human-like flick
   */
  private makeMove(round: number, ballIndex: number, start: { x: number; y: number }, tiltX: number) {
    const rollInput = this.generateFlick(round, ballIndex, start, tiltX);
    this.onMoveCallback?.(rollInput);
  }

  /**
   * Generate flick - back to working straight shot with configurable randomness
   */
  private generateFlick(round: number, ballIndex: number, start: { x: number; y: number }, tiltX: number): RollInput {
    
    let rawVx = 30;
    let rawVy = this.config.baseVelocityYMin;
    let spin = 0;

    // Apply randomness only if ranges are > 0
    if (this.config.baseVelocityXRange > 0) {
      rawVx += (Math.random() - 0.5) * this.config.baseVelocityXRange;
    }

    if (this.config.baseVelocityYRange > 0) {
      rawVy += (Math.random() - 0.5) * this.config.baseVelocityYRange;
    }

    if (this.config.spinRange > 0) {
      spin = (Math.random() - 0.5) * this.config.spinRange;
    }

    // Apply speedScale like human input
    const velFracPerSec = {
      x: rawVx * this.config.speedScale,
      y: rawVy * this.config.speedScale
    };

    console.log(`AI Shot: rawVx=${rawVx.toFixed(1)}, rawVy=${rawVy.toFixed(1)}, scaledVx=${velFracPerSec.x.toFixed(1)}, scaledVy=${velFracPerSec.y.toFixed(1)}, spin=${spin.toFixed(2)}`);

    return {
      kind: 'roll_input',
      round,
      ballIndex,
      start: { x: start.x, y: start.y },
      velFracPerSec,
      spin,
      tiltX,
      t0: performance.now()
    };
  }

  /**
   * Get AI player info for game setup
   */
  getPlayerInfo() {
    return {
      userId: this.userId,
      userName: this.userName
    };
  }
}